import { useMemo } from 'react';
import * as R from 'remeda';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { z } from 'zod';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
} from '@rossum/ui/material';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { ThemeType } from '../../../constants/values';
import {
  getLocaleOptions,
  LOCALE_DEFAULT_SENTINEL,
} from '../../../i18n/config';
import { safeOrganizationSelector } from '../../../redux/modules/organization/selectors';
import { additionalLocalesEnabledSelector } from '../../../redux/modules/organizationGroup/selectors';
import {
  PartialUser,
  updateUser as updateUserAction,
} from '../../../redux/modules/user/actions';
import { splitFullName } from '../../../redux/modules/user/helpers';
import { userSelector } from '../../../redux/modules/user/selectors';

type ThemeChoices = ThemeType | 'organization' | 'default';

type ThemeFieldOption = { value: ThemeChoices; label: string };

const themeSchema = z.enum(['dark', 'white']);

const resolveIncomingTheme = ({
  userTheme,
  orgTheme,
}: {
  userTheme?: ThemeType;
  orgTheme?: ThemeType;
}) => {
  const defaultTheme = orgTheme ? 'organization' : 'default';
  const parsed = themeSchema.safeParse(userTheme);
  return parsed.success ? parsed.data : defaultTheme;
};

const resolveOutgoingTheme = (selectedThemeChoice: ThemeChoices) => {
  return selectedThemeChoice === 'dark' || selectedThemeChoice === 'white'
    ? selectedThemeChoice
    : undefined;
};

export const PersonalInformationForm = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const organization = useSelector(safeOrganizationSelector);
  const additionalLocalesEnabled = useSelector(
    additionalLocalesEnabledSelector
  );
  const localeOptions = useMemo(
    () => getLocaleOptions({ additionalLocalesEnabled }),
    [additionalLocalesEnabled]
  );

  const hasOrgTheme = !!organization?.uiSettings.theme;

  const initialThemeChoice = resolveIncomingTheme({
    userTheme: user.uiSettings.theme,
    orgTheme: organization?.uiSettings.theme,
  });

  const themeFieldOptions = useMemo<Array<ThemeFieldOption>>(() => {
    const defaultOption: ThemeFieldOption = hasOrgTheme
      ? {
          value: 'organization',
          label: intl.formatMessage({
            id: 'features.personalSettings.theme.organization',
          }),
        }
      : {
          value: 'default',
          label: intl.formatMessage({
            id: 'features.personalSettings.theme.default',
          }),
        };

    return [
      defaultOption,
      {
        value: 'dark',
        label: intl.formatMessage({
          id: 'features.personalSettings.theme.dark',
        }),
      },
      {
        value: 'white',
        label: intl.formatMessage({
          id: 'features.personalSettings.theme.white',
        }),
      },
    ];
  }, [hasOrgTheme, intl]);

  const personalSettingsSchema = z.object({
    name: z
      .string()
      .trim()
      .min(1, {
        message: intl.formatMessage({
          id: 'containers.settings.users.addUser.name.error',
        }),
      }),
    locale: z.string().optional(),
    theme: z.enum(['white', 'dark', 'organization', 'default']).optional(),
    showConfidenceScore: z.boolean().optional(),
  });

  type PersonalSettingsFormValues = z.infer<typeof personalSettingsSchema>;

  const defaultValues: PersonalSettingsFormValues = {
    name: R.pipe(
      [user.firstName, user.lastName],
      R.filter(R.isTruthy),
      R.join(' ')
    ),
    locale: user.uiSettings.locale ?? LOCALE_DEFAULT_SENTINEL,
    theme: initialThemeChoice,
    showConfidenceScore: user.uiSettings.showConfidenceScore,
  };

  const { handleSubmit, control } = useForm({
    mode: 'onSubmit',
    defaultValues,
    resolver: zodResolver(personalSettingsSchema),
  });

  const onSubmit = (data: PersonalSettingsFormValues) => {
    const payload: PartialUser = {
      firstName: splitFullName(data.name).firstName,
      lastName: splitFullName(data.name).lastName,
      uiSettings: {
        locale:
          data.locale === LOCALE_DEFAULT_SENTINEL
            ? null
            : (data.locale ?? null),
        theme: resolveOutgoingTheme(data.theme ?? 'default'),
        showConfidenceScore: data.showConfidenceScore,
      },
    };
    dispatch(updateUserAction(payload, { withMessage: true }));
  };

  return (
    <Stack spacing={4} component="form" onSubmit={handleSubmit(onSubmit)}>
      <ContentGroup
        title={intl.formatMessage({
          id: 'features.personalSettings.personalInformation',
        })}
        description={intl.formatMessage({
          id: 'features.personalSettings.personalInformation.description',
        })}
      >
        <TextFieldControl
          ControllerProps={{ control, name: 'name' }}
          label={intl.formatMessage({
            id: 'containers.settings.users.addUser.name.label',
          })}
          placeholder={intl.formatMessage({
            id: 'containers.settings.users.addUser.name.placeholder',
          })}
          FieldLabelProps={{ layout: 'floating' }}
          inputProps={{
            'data-cy': 'my-profile-name-input',
          }}
        />

        <TextFieldControl
          ControllerProps={{ control, name: 'locale' }}
          label={intl.formatMessage({
            id: 'containers.settings.users.addUser.locale.label',
          })}
          placeholder={intl.formatMessage({
            id: 'containers.settings.users.addUser.locale.placeholder',
          })}
          select
          autoComplete="off"
          FieldLabelProps={{ layout: 'floating' }}
        >
          <MenuItem
            key={LOCALE_DEFAULT_SENTINEL}
            value={LOCALE_DEFAULT_SENTINEL}
            data-cy="locale-option-default"
          >
            {intl.formatMessage({
              id: 'containers.settings.users.addUser.locale.default',
            })}
          </MenuItem>
          {localeOptions.map(locale => (
            <MenuItem
              key={locale.label}
              value={locale.value}
              data-cy={`locale-option-${locale.value}`}
            >
              {locale.label}
            </MenuItem>
          ))}
        </TextFieldControl>

        <TextFieldControl
          ControllerProps={{ control, name: 'theme' }}
          label={intl.formatMessage({
            id: 'features.personalSettings.theme',
          })}
          placeholder={intl.formatMessage({
            id: 'features.personalSettings.theme.placeholder',
          })}
          select
          autoComplete="off"
          FieldLabelProps={{ layout: 'floating' }}
        >
          {themeFieldOptions.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              data-cy={`theme-option-${option.value}`}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextFieldControl>

        <Controller
          control={control}
          name="showConfidenceScore"
          render={({ field }) => (
            <FormControlLabel
              sx={{ alignItems: 'flex-start' }}
              label={
                <Stack direction="column">
                  <Typography fontWeight="bold">
                    {intl.formatMessage({
                      id: 'features.personalSettings.showConfidenceScore.title',
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({
                      id: 'features.personalSettings.showConfidenceScore.description',
                    })}
                  </Typography>
                </Stack>
              }
              control={
                <Checkbox
                  sx={{
                    paddingTop: 0,
                    paddingLeft: 0,
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                  disableRipple
                  {...field}
                  checked={field.value ?? false}
                />
              }
            />
          )}
        />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            data-cy="my-profile-update-settings"
          >
            {intl.formatMessage({
              id: 'containers.settings.users.update',
            })}
          </Button>
        </Stack>
      </ContentGroup>
    </Stack>
  );
};
